import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const areas = [
  {
    name: 'London',
    tag: 'Greater London',
    desc: 'Full coverage across all London boroughs — from central zones to outer suburbs. Solar panel and battery storage specialists for homes and commercial premises.',
    icon: 'ri-building-2-line',
    highlights: ['All 32 Boroughs', 'Commercial Solar', 'Residential Homes', 'Battery Retrofit'],
    imageUrl: 'https://public.readdy.ai/ai/img_res/e4fabd71772d84f06bf63095d12a3cdc.jpg',
  },
  {
    name: 'Essex',
    tag: 'County Essex',
    desc: 'Covering Chelmsford, Colchester, Southend, Basildon, Brentwood and surrounding areas — solar panel and battery storage installations for homes and businesses.',
    icon: 'ri-home-4-line',
    highlights: ['Chelmsford', 'Colchester', 'Southend-on-Sea', 'Basildon & Brentwood'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Essex%20English%20countryside%20with%20charming%20village%20high%20street%20and%20modern%20residential%20homes%2C%20green%20fields%2C%20classic%20British%20suburban%20town%2C%20bright%20sunny%20weather%2C%20clean%20UK%20street%20photography&width=500&height=340&seq=coverage-essex-v1&orientation=landscape',
  },
  {
    name: 'Suffolk',
    tag: 'County Suffolk',
    desc: 'Ipswich, Bury St Edmunds, Lowestoft and beyond. We install solar panels and home battery storage for homeowners and businesses across the full county.',
    icon: 'ri-landscape-line',
    highlights: ['Ipswich', 'Bury St Edmunds', 'Lowestoft', 'Newmarket & Sudbury'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Suffolk%20England%20beautiful%20countryside%20village%20with%20period%20cottages%20and%20rural%20landscape%2C%20rolling%20green%20hills%2C%20idyllic%20British%20scenery%2C%20warm%20afternoon%20light%2C%20charming%20English%20town&width=500&height=340&seq=coverage-suffolk-v1&orientation=landscape',
  },
  {
    name: 'Cambridge',
    tag: 'Cambridgeshire',
    desc: 'Cambridge city and throughout Cambridgeshire. From new-build homes to period properties — solar and battery installed by MCS-accredited engineers.',
    icon: 'ri-graduation-cap-line',
    highlights: ['Cambridge City', 'Peterborough', 'Ely & Huntingdon', 'New Builds & Period Homes'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Cambridge%20England%20city%20centre%20with%20university%20buildings%20and%20bicycles%2C%20historic%20architecture%2C%20beautiful%20British%20city%2C%20punting%20river%2C%20classic%20college%20facades%2C%20bright%20clear%20daylight%20photography&width=500&height=340&seq=coverage-cambridge-v1&orientation=landscape',
  },
];

export default function CoverageSection() {
  const headingAnim = useScrollAnimation();
  const commercialAnim = useScrollAnimation();
  const cardsAnim = useScrollAnimation();

  return (
    <section id="coverage" className="bg-white py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Heading */}
        <div
          ref={headingAnim.ref}
          className={`text-center mb-10 transition-all duration-700 ${headingAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="text-xs font-bold uppercase tracking-widest text-[#4CAF50] mb-2">COVERAGE AREAS</div>
          <h2 className="font-heading font-black text-[#1F2933] text-2xl md:text-3xl">
            Covering London, Essex,<br className="hidden md:block" /> Suffolk &amp; Cambridge
          </h2>
          <p className="text-gray-500 text-sm md:text-base mt-3 max-w-2xl mx-auto leading-relaxed">
            We specialise in both <strong className="text-[#1F2933]">commercial</strong> and <strong className="text-[#1F2933]">residential</strong> solar panel and battery storage installation across four key regions. Whether you&apos;re a homeowner, landlord, or business — our MCS-accredited engineers cover your area.
          </p>
        </div>

        {/* Commercial vs Residential callout */}
        <div
          ref={commercialAnim.ref}
          className={`mb-10 transition-all duration-700 ${commercialAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          {/* Mobile: swipe */}
          <div className="sm:hidden">
            <div className="flex items-center justify-end mb-3 px-0.5">
              <span className="text-[11px] text-[#4CAF50] font-semibold flex items-center gap-1">
                <i className="ri-drag-move-line text-xs" /> Swipe
              </span>
            </div>
            <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory pb-3 [&::-webkit-scrollbar]:hidden -mx-4 px-4">
              {/* Commercial */}
              <div className="snap-start flex-shrink-0 w-[85vw] bg-[#145A32] rounded-2xl p-6 flex gap-4 items-start">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/10 flex-shrink-0">
                  <i className="ri-building-line text-[#7AC943] text-2xl" />
                </div>
                <div>
                  <div className="text-[#7AC943] text-xs font-bold uppercase tracking-widest mb-1">COMMERCIAL</div>
                  <h3 className="font-bold text-white text-base mb-1.5">Businesses, Warehouses &amp; Offices</h3>
                  <p className="text-white/70 text-sm leading-relaxed">
                    Roof-mounted solar arrays, battery storage and on-site generation for commercial premises. Reduce operating costs and meet sustainability targets with MCS-certified installations.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {['Offices', 'Warehouses', 'Retail', 'Factories', 'Schools'].map(t => (
                      <span key={t} className="text-[10px] bg-white/10 text-white/80 font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
              {/* Residential */}
              <div className="snap-start flex-shrink-0 w-[85vw] bg-[#f7f8fa] rounded-2xl p-6 flex gap-4 items-start border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#145A32]/10 flex-shrink-0">
                  <i className="ri-home-heart-line text-[#145A32] text-2xl" />
                </div>
                <div>
                  <div className="text-[#145A32] text-xs font-bold uppercase tracking-widest mb-1">RESIDENTIAL</div>
                  <h3 className="font-bold text-[#1F2933] text-base mb-1.5">Homes, New Builds &amp; Flats</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Solar panels and battery storage installed the same week. Cut your electricity bills by up to 70%, generate free energy and protect yourself from rising tariffs. Finance available.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {['Detached Homes', 'Semi-Detached', 'New Builds', 'Terraced', 'Bungalows'].map(t => (
                      <span key={t} className="text-[10px] bg-[#145A32]/10 text-[#145A32] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* sm+: side-by-side grid */}
          <div className="hidden sm:grid grid-cols-2 gap-4">
            <div className="bg-[#145A32] rounded-2xl p-6 flex gap-4 items-start">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/10 flex-shrink-0">
                <i className="ri-building-line text-[#7AC943] text-2xl" />
              </div>
              <div>
                <div className="text-[#7AC943] text-xs font-bold uppercase tracking-widest mb-1">COMMERCIAL</div>
                <h3 className="font-bold text-white text-base mb-1.5">Businesses, Warehouses &amp; Offices</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Roof-mounted solar arrays, battery storage and on-site generation for commercial premises. Reduce operating costs and meet sustainability targets with MCS-certified installations.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {['Offices', 'Warehouses', 'Retail', 'Factories', 'Schools'].map(t => (
                    <span key={t} className="text-[10px] bg-white/10 text-white/80 font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">{t}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="bg-[#f7f8fa] rounded-2xl p-6 flex gap-4 items-start border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#145A32]/10 flex-shrink-0">
                <i className="ri-home-heart-line text-[#145A32] text-2xl" />
              </div>
              <div>
                <div className="text-[#145A32] text-xs font-bold uppercase tracking-widest mb-1">RESIDENTIAL</div>
                <h3 className="font-bold text-[#1F2933] text-base mb-1.5">Homes, New Builds &amp; Flats</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Solar panels and battery storage installed the same week. Cut your electricity bills by up to 70%, generate free energy and protect yourself from rising tariffs. Finance available.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {['Detached Homes', 'Semi-Detached', 'New Builds', 'Terraced', 'Bungalows'].map(t => (
                    <span key={t} className="text-[10px] bg-[#145A32]/10 text-[#145A32] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Area cards */}
        <div ref={cardsAnim.ref}>
          {/* Mobile: swipe slider */}
          <div className="lg:hidden">
            <div className="flex items-center justify-end mb-3 px-0.5">
              <span className="text-[11px] text-[#4CAF50] font-semibold flex items-center gap-1">
                <i className="ri-drag-move-line text-xs" /> Swipe areas
              </span>
            </div>
            <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory pb-4 [&::-webkit-scrollbar]:hidden -mx-4 px-4">
              {areas.map((area, i) => (
                <div
                  key={area.name}
                  className="snap-start flex-shrink-0 w-[72vw] bg-white rounded-2xl border border-gray-100 overflow-hidden"
                >
                  <div className="h-32 overflow-hidden">
                    <img
                      src={area.imageUrl}
                      alt={area.name}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#145A32]/10 flex-shrink-0">
                        <i className={`${area.icon} text-[#145A32] text-sm`} />
                      </div>
                      <div>
                        <div className="font-black text-[#1F2933] text-base leading-tight">{area.name}</div>
                        <div className="text-[10px] text-gray-400">{area.tag}</div>
                      </div>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-3">{area.desc}</p>
                    <ul className="flex flex-col gap-1">
                      {area.highlights.map(h => (
                        <li key={h} className="flex items-center gap-1.5 text-[11px] text-gray-600">
                          <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                            <i className="ri-sun-line text-[#4CAF50] text-xs" />
                          </div>
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-1.5 mt-1">
              {areas.map((_, i) => (
                <div
                  key={i}
                  className={`rounded-full transition-all ${i === 0 ? 'w-4 h-1.5 bg-[#4CAF50]' : 'w-1.5 h-1.5 bg-gray-300'}`}
                />
              ))}
            </div>
          </div>

          {/* Desktop: 4-col grid */}
          <div className="hidden lg:grid grid-cols-4 gap-4">
            {areas.map((area, i) => (
              <div
                key={area.name}
                style={{ transitionDelay: `${i * 100}ms` }}
                className={`bg-white rounded-2xl border border-gray-100 overflow-hidden hover:-translate-y-1 hover:border-[#4CAF50]/40 transition-all duration-300 group ${cardsAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <div className="h-36 overflow-hidden">
                  <img
                    src={area.imageUrl}
                    alt={area.name}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#145A32]/10 flex-shrink-0">
                      <i className={`${area.icon} text-[#145A32] text-sm`} />
                    </div>
                    <div>
                      <div className="font-black text-[#1F2933] text-base leading-tight">{area.name}</div>
                      <div className="text-[10px] text-gray-400">{area.tag}</div>
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed mb-3">{area.desc}</p>
                  <ul className="flex flex-col gap-1">
                    {area.highlights.map(h => (
                      <li key={h} className="flex items-center gap-1.5 text-[11px] text-gray-600">
                        <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                          <i className="ri-sun-line text-[#4CAF50] text-xs" />
                        </div>
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA strip */}
        <div className={`mt-8 bg-[#f7f8fa] border border-gray-100 rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4 transition-all duration-700 delay-400 ${cardsAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#145A32]/10 flex-shrink-0">
              <i className="ri-map-pin-line text-[#145A32] text-lg" />
            </div>
            <div>
              <div className="font-bold text-[#1F2933] text-sm">Not sure if we cover your area?</div>
              <div className="text-gray-500 text-xs">Enter your postcode and we&apos;ll confirm solar coverage instantly.</div>
            </div>
          </div>
          <button
            onClick={() => document.getElementById('quote-section')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-gradient-to-r from-[#145A32] to-[#4CAF50] text-white font-bold px-6 py-3 rounded-full text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            Check my postcode →
          </button>
        </div>
      </div>
    </section>
  );
}
